import React from "react";
import { Box, Typography, CardContent, Grid, useTheme } from "@mui/material";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { StyledCard } from "../../common/StyledCard";
import { getEntityMeta } from "../../../helpers/dashboardHelpers";
import { AttendanceStatsDto } from "../../../shared/dtos/attendanceRecord/AttendanceStatsDto";
import { AttendanceStatus } from "../../../shared/enums/AttendanceStatus";

interface StatusMeta {
  value: AttendanceStatus;
  label: string;
  color: "success" | "warning" | "error" | "info" | "secondary";
}

const ATTENDANCE_META_LIST: StatusMeta[] = [
  { value: "PRESENT" as AttendanceStatus, label: "Present", color: "success" },
  { value: "LATE" as AttendanceStatus, label: "Late", color: "warning" },
  {
    value: "ABSENT_UNEXCUSED" as AttendanceStatus,
    label: "Unexcused",
    color: "error",
  },
  {
    value: "ABSENT_EXCUSED" as AttendanceStatus,
    label: "Excused",
    color: "info",
  },
  {
    value: "LEFT_EARLY" as AttendanceStatus,
    label: "Left Early",
    color: "secondary",
  },
];

interface AttendanceWidgetProps {
  data: AttendanceStatsDto;
}

const AttendanceWidget: React.FC<AttendanceWidgetProps> = ({ data }) => {
  const meta = getEntityMeta("ATTENDANCE_RECORD");
  const Icon = meta.icon;
  const theme = useTheme();

  const colorMap: Record<string, string> = {
    success: theme.palette.success.main,
    error: theme.palette.error.main,
    warning: theme.palette.warning.main,
    info: theme.palette.info.main,
    secondary: theme.palette.secondary.main,
  };

  const counts = data.counts || {};
  const presentCount = counts["PRESENT" as AttendanceStatus] || 0;
  const lateCount = counts["LATE" as AttendanceStatus] || 0;
  const unexcusedCount = counts["ABSENT_UNEXCUSED" as AttendanceStatus] || 0;
  const excusedCount = counts["ABSENT_EXCUSED" as AttendanceStatus] || 0;
  const totalAbsentCount = unexcusedCount + excusedCount;

  const gaugeChartData = [
    { name: "Present", value: presentCount, color: colorMap.success },
    { name: "Late", value: lateCount, color: colorMap.warning },
    { name: "Absent", value: totalAbsentCount, color: colorMap.error },
  ].filter((item) => item.value > 0);

  const isDataEmpty = data.total === 0;
  const renderData = isDataEmpty
    ? [{ name: "No Data", value: 1, color: theme.palette.grey[300] }]
    : gaugeChartData;

  return (
    <StyledCard sx={{ height: "100%" }}>
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 1,
              bgcolor: `${meta.color}15`,
              color: meta.color,
              display: "flex",
              mr: 2,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {meta.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Total Records: {data.total}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ height: 160, width: "100%", position: "relative" }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={renderData}
                cx="50%"
                cy="100%"
                startAngle={180}
                endAngle={0}
                innerRadius={60}
                outerRadius={80}
                paddingAngle={2}
                dataKey="value"
              >
                {renderData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: "50%",
              transform: "translateX(-50%)",
              textAlign: "center",
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontWeight: "bold",
                color: isDataEmpty ? "text.disabled" : "text.primary",
              }}
            >
              {isDataEmpty ? "N/A" : `${data.attendanceRate}%`}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Rate
            </Typography>
          </Box>
        </Box>

        <Grid container spacing={1} sx={{ mt: 2, textAlign: "center" }}>
          {ATTENDANCE_META_LIST.map((status) => (
            <Grid key={status.value}>
              <Typography variant="h6" sx={{ color: colorMap[status.color] }}>
                {counts[status.value] || 0}
              </Typography>
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {status.label}
              </Typography>
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default AttendanceWidget;
